import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import CustomSlider from './CustomSlider';

/**
 * Componente CustomizationSliders
 * 
 * Gerencia os 5 sliders de personalização: creativity, tone, scope_topics, complexity, foundation_depth
 * 
 * Props:
 * - initialValues: objeto com os valores iniciais dos parâmetros
 * - onChange: callback chamado quando qualquer parâmetro muda, recebe o objeto completo
 * - disabled: se true, todos os sliders ficam desabilitados
 * 
 * Métodos expostos via ref:
 * - getValues(): retorna o objeto atual com todos os valores
 * - setValues(newValues): atualiza os valores programaticamente
 * - resetToDefaults(): reseta para valores padrão
 */
const CustomizationSliders = forwardRef(({ 
  initialValues = {
    creativity: 2,
    tone: 2,
    scope_topics: 2,
    complexity: 2,
    foundation_depth: 2,
  },
  onChange = null,
  disabled = false
}, ref) => {
  const { t } = useTranslation('jobs');
  
  // Estado interno para gerenciar os valores dos sliders
  const [values, setValues] = useState(initialValues);
  
  // Sincronizar com valores iniciais quando mudarem
  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);
  
  // Função para atualizar um parâmetro específico
  const handleParameterChange = (paramName, value) => {
    const newValues = {
      ...values,
      [paramName]: value,
    };
    setValues(newValues);
    
    // Chamar callback externo se fornecido
    if (onChange) {
      onChange(newValues);
    }
  };
  
  // Expor métodos via ref
  useImperativeHandle(ref, () => ({
    getValues: () => values,
    setValues: (newValues) => {
      setValues(newValues);
      if (onChange) {
        onChange(newValues);
      }
    },
    resetToDefaults: () => {
      const defaultValues = {
        creativity: 2,
        tone: 2,
        scope_topics: 2,
        complexity: 2,
        foundation_depth: 2,
      };
      setValues(defaultValues);
      if (onChange) {
        onChange(defaultValues);
      }
    }
  }), [values, onChange]);
  
  // Verificar se customização está desabilitada baseado na lógica existente
  const isCustomizationDisabled = values.creativity === 1 && values.scope_topics === 1;
  
  return (
    <div className="customization-sliders">
      {/* Slider: Scope Topics - sempre vem primeiro pois afeta a lógica de desabilitação */}
      <CustomSlider
        id="scope_topics"
        value={values.scope_topics}
        min={1}
        max={3}
        levels={t('contestacaoCreate.customization.scopeTopics.levels', { returnObjects: true })}
        tooltips={t('contestacaoCreate.customization.scopeTopics.tooltips', { returnObjects: true })}
        title={t('contestacaoCreate.customization.scopeTopics.title')}
        description={t('contestacaoCreate.customization.scopeTopics.description')}
        onChange={(e) => {
          if (!e || !e.target) {
            console.warn('Event target is null in scope_topics onChange');
            return;
          }
          handleParameterChange('scope_topics', parseInt(e.target.value));
        }}
        disabled={disabled}
      />
      
      {/* Slider: Creativity - segundo pois também afeta a lógica de desabilitação */}
      <CustomSlider
        id="creativity"
        value={values.creativity}
        min={1}
        max={3}
        levels={t('contestacaoCreate.customization.creativity.levels', { returnObjects: true })}
        tooltips={t('contestacaoCreate.customization.creativity.tooltips', { returnObjects: true })}
        title={t('contestacaoCreate.customization.creativity.title')}
        description={t('contestacaoCreate.customization.creativity.description')}
        onChange={(e) => {
          if (!e || !e.target) {
            console.warn('Event target is null in creativity onChange');
            return;
          }
          handleParameterChange('creativity', parseInt(e.target.value));
        }}
        disabled={disabled}
      />
      
      {/* Slider: Tone - pode ser desabilitado pela lógica de customização */}
      <CustomSlider
        id="tone"
        value={values.tone}
        min={1}
        max={3}
        levels={t('contestacaoCreate.customization.tone.levels', { returnObjects: true })}
        tooltips={t('contestacaoCreate.customization.tone.tooltips', { returnObjects: true })}
        title={t('contestacaoCreate.customization.tone.title')}
        description={t('contestacaoCreate.customization.tone.description')}
        onChange={(e) => {
          if (!e || !e.target) {
            console.warn('Event target is null in tone onChange');
            return;
          }
          handleParameterChange('tone', parseInt(e.target.value));
        }}
        disabled={disabled || isCustomizationDisabled}
        disabledExplanation={isCustomizationDisabled ? t('contestacaoCreate.customization.disabledExplanation') : null}
      />
      
      {/* Slider: Complexity - pode ser desabilitado pela lógica de customização */}
      <CustomSlider
        id="complexity"
        value={values.complexity}
        min={1}
        max={3}
        levels={t('contestacaoCreate.customization.complexity.levels', { returnObjects: true })}
        tooltips={t('contestacaoCreate.customization.complexity.tooltips', { returnObjects: true })}
        title={t('contestacaoCreate.customization.complexity.title')}
        description={t('contestacaoCreate.customization.complexity.description')}
        onChange={(e) => {
          if (!e || !e.target) {
            console.warn('Event target is null in complexity onChange');
            return;
          }
          handleParameterChange('complexity', parseInt(e.target.value));
        }}
        disabled={disabled || isCustomizationDisabled}
        disabledExplanation={isCustomizationDisabled ? t('contestacaoCreate.customization.disabledExplanation') : null}
      />
      
      {/* Slider: Foundation Depth - pode ser desabilitado pela lógica de customização */}
      <CustomSlider
        id="foundation_depth"
        value={values.foundation_depth}
        min={1}
        max={3}
        levels={t('contestacaoCreate.customization.foundationDepth.levels', { returnObjects: true })}
        tooltips={t('contestacaoCreate.customization.foundationDepth.tooltips', { returnObjects: true })}
        title={t('contestacaoCreate.customization.foundationDepth.title')}
        description={t('contestacaoCreate.customization.foundationDepth.description')}
        onChange={(e) => {
          if (!e || !e.target) {
            console.warn('Event target is null in foundation_depth onChange');
            return;
          }
          handleParameterChange('foundation_depth', parseInt(e.target.value));
        }}
        disabled={disabled || isCustomizationDisabled}
        disabledExplanation={isCustomizationDisabled ? t('contestacaoCreate.customization.disabledExplanation') : null}
      />
    </div>
  );
});

CustomizationSliders.displayName = 'CustomizationSliders';

export default CustomizationSliders;

/*
EXEMPLO DE USO:

// Uso básico com callback
const [customizationParams, setCustomizationParams] = useState({
  creativity: 2,
  tone: 2,
  scope_topics: 2,
  complexity: 2,
  foundation_depth: 2,
});

<CustomizationSliders
  initialValues={customizationParams}
  onChange={setCustomizationParams}
/>

// Uso com ref para controle programático
const slidersRef = useRef();

const handleGetValues = () => {
  const currentValues = slidersRef.current.getValues();
  console.log('Valores atuais:', currentValues);
};

const handleSetValues = () => {
  slidersRef.current.setValues({
    creativity: 3,
    tone: 1,
    scope_topics: 2,
    complexity: 2,
    foundation_depth: 1,
  });
};

const handleReset = () => {
  slidersRef.current.resetToDefaults();
};

<CustomizationSliders
  ref={slidersRef}
  initialValues={customizationParams}
  onChange={setCustomizationParams}
/>

// Uso com desabilitação
<CustomizationSliders
  initialValues={customizationParams}
  onChange={setCustomizationParams}
  disabled={true}
/>
*/
